import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Megaphone, Save, Loader2, ExternalLink, Linkedin, Plus, Trash2, GripVertical, Code } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

const LinkedInActualitePage = () => {
  const [linkedinUrl, setLinkedinUrl] = useState('https://www.linkedin.com/company/sfoalc/');
  const [embeds, setEmbeds] = useState([]);
  const [newEmbed, setNewEmbed] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/site-content/linkedin`);
        if (res.data?.data) {
          setLinkedinUrl(res.data.data.url || 'https://www.linkedin.com/company/sfoalc/');
          setEmbeds(res.data.data.embeds || []);
        }
      } catch { /* keep default */ }
      setLoading(false);
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-content/linkedin`, {
        data: { url: linkedinUrl, embeds },
      });
      toast.success('Configuration LinkedIn sauvegardée');
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const addEmbed = () => {
    if (!newEmbed.trim()) return;
    // Extract src from iframe if full HTML is pasted
    let embedUrl = newEmbed.trim();
    const srcMatch = embedUrl.match(/src=["']([^"']+)["']/);
    if (srcMatch) embedUrl = srcMatch[1];
    setEmbeds(prev => [...prev, { url: embedUrl, title: `Post LinkedIn ${prev.length + 1}` }]);
    setNewEmbed('');
  };

  const removeEmbed = (idx) => {
    setEmbeds(prev => prev.filter((_, i) => i !== idx));
  };

  if (loading) return <AdminLayout><div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0077B5]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-5xl mx-auto" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="linkedin-admin">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0077B5] to-[#004182] flex items-center justify-center">
              <Linkedin size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Actualité LinkedIn</h1>
              <p className="text-xs text-gray-500">Intégrez les posts LinkedIn de la SFOALC sur le site</p>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0077B5] hover:bg-[#005f8d] text-white text-sm font-bold disabled:opacity-50 transition-colors" data-testid="save-linkedin">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Sauvegarder
          </button>
        </div>

        {/* URL du profil */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">URL de la page LinkedIn SFOALC</label>
          <div className="flex gap-3">
            <input value={linkedinUrl} onChange={e => setLinkedinUrl(e.target.value)}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#0077B5] focus:ring-2 focus:ring-[#0077B5]/10 outline-none"
              placeholder="https://www.linkedin.com/company/sfoalc/" data-testid="linkedin-url-input" />
            <a href={linkedinUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
              <ExternalLink size={14} /> Ouvrir
            </a>
          </div>
        </div>

        {/* Embed Posts */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-gray-700">Posts LinkedIn intégrés</h2>
              <p className="text-xs text-gray-400 mt-0.5">Collez l'URL ou le code iframe d'un post LinkedIn</p>
            </div>
          </div>

          {/* Add new embed */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Code size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={newEmbed} onChange={e => setNewEmbed(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#0077B5] outline-none"
                placeholder="Collez le code iframe ou URL du post LinkedIn..."
                onKeyDown={e => { if (e.key === 'Enter') addEmbed(); }}
                data-testid="add-embed-input" />
            </div>
            <button onClick={addEmbed}
              className="flex items-center gap-1 px-4 py-2.5 rounded-xl bg-[#0077B5] text-white text-sm font-bold hover:bg-[#005f8d]" data-testid="add-embed-btn">
              <Plus size={14} /> Ajouter
            </button>
          </div>

          {/* Embeds list */}
          <div className="space-y-3">
            {embeds.map((embed, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl group" data-testid={`embed-item-${i}`}>
                <GripVertical size={16} className="text-gray-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <input value={embed.title || ''} onChange={e => {
                    const updated = [...embeds];
                    updated[i] = { ...updated[i], title: e.target.value };
                    setEmbeds(updated);
                  }}
                    className="text-sm font-semibold text-gray-900 bg-transparent border-none outline-none w-full"
                    placeholder="Titre du post..." />
                  <p className="text-xs text-gray-400 truncate">{embed.url}</p>
                </div>
                <button onClick={() => removeEmbed(i)}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid={`remove-embed-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {embeds.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Linkedin size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Aucun post intégré</p>
                <p className="text-xs mt-1">Sur LinkedIn, cliquez sur "..." d'un post → "Intégrer ce post" et collez le code ici</p>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        {embeds.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-sm font-bold text-gray-700 mb-4">Aperçu</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {embeds.map((embed, i) => (
                <div key={i} className="rounded-xl border border-gray-200 overflow-hidden">
                  <iframe src={embed.url} width="100%" height="400" frameBorder="0" allowFullScreen
                    title={embed.title || `Post ${i + 1}`}
                    className="w-full" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help */}
        <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100 mt-6">
          <div className="flex items-start gap-3">
            <Megaphone size={18} className="text-[#0077B5] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-1">Comment intégrer un post LinkedIn</p>
              <ol className="text-xs text-gray-600 space-y-1.5 list-decimal ml-4">
                <li>Allez sur la page LinkedIn de la SFOALC</li>
                <li>Cliquez sur les "..." en haut à droite du post que vous souhaitez intégrer</li>
                <li>Cliquez sur "Intégrer ce post"</li>
                <li>Copiez le code iframe fourni par LinkedIn</li>
                <li>Collez-le dans le champ ci-dessus</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LinkedInActualitePage;
